import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { Idea, COLLECTION_NAMES } from '../types';

/**
 * Hook for Ideas data with Firestore
 * Manages ideas and fixes tracking
 */
export function useIdeas(userId: string | undefined) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [loading, setLoading] = useState(true);

  // Load ideas with real-time listener
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const ideasQuery = query(
      collection(db, COLLECTION_NAMES.IDEAS),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      ideasQuery,
      (snapshot) => {
        const ideasData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            completedAt: data.completedAt?.toDate?.() || data.completedAt,
          } as Idea;
        });
        setIdeas(ideasData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading ideas:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Create idea
  const createIdea = async (formData: {
    title: string;
    description: string;
    type: 'idea' | 'fix';
  }) => {
    if (!userId) return;

    try {
      const ideaData = {
        ...formData,
        status: 'new' as const,
        createdBy: userId,
        createdAt: Timestamp.now(),
      };

      await addDoc(collection(db, COLLECTION_NAMES.IDEAS), ideaData);
    } catch (error) {
      console.error('Error creating idea:', error);
      throw error;
    }
  };

  // Update idea (mark as completed)
  const updateIdea = async (ideaId: string, updates: Partial<Idea>) => {
    try {
      const ideaRef = doc(db, COLLECTION_NAMES.IDEAS, ideaId);
      const updateData: any = {
        ...updates,
      };

      // If marking as completed, set completedAt and completedBy
      if (updates.status === 'completed' && userId) {
        updateData.completedAt = Timestamp.now();
        updateData.completedBy = userId;
      }

      await updateDoc(ideaRef, updateData);
    } catch (error) {
      console.error('Error updating idea:', error);
      throw error;
    }
  };

  // Delete idea (super users only - check in component)
  const deleteIdea = async (ideaId: string) => {
    try {
      const ideaRef = doc(db, COLLECTION_NAMES.IDEAS, ideaId);
      await deleteDoc(ideaRef);
    } catch (error) {
      console.error('Error deleting idea:', error);
      throw error;
    }
  };

  return {
    ideas,
    loading,
    createIdea,
    updateIdea,
    deleteIdea,
  };
}

